import { useOffline } from '../contexts/OfflineContext';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useModalBackButton from '../hooks/useModalBackButton';
import useToast from '../hooks/useToast';
import DeferredWorkQueue from '../components/DeferredWorkQueue';
import ModalShell from '../components/ModalShell';
import {
  Package,
  Search,
  SearchX,
  Building2,
  Calendar,
  ChevronRight,
  User,
  MapPin,
  BriefcaseBusiness,
  Clock,
  Check,
  MessageCircle,
  Ruler,
  Scale,
  ShoppingCart,
  Star,
  Siren,
  Truck,
  ArrowUp,
  RotateCcw,
  Phone,
  Zap,
  X,
  XCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { API_URL } from '../config';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatTimezoneDate } from '../utils/timezone';
import { USER_STAGE_ORDER, STAGES_CONFIG, STAGE_TAB_STYLES, getStagesFromRoles } from '../utils/stages';
import { useDryCleaningEnabled } from '../utils/featureFlags';
import { useStageContext } from '../contexts/StageContext';
import { useInteractionPreferences } from '../contexts/InteractionPreferencesContext';
import { haptic } from '../utils/vibration';
import { getClientVersionHeaders } from '../utils/clientVersion';
import {
  readOfflineMyOrdersList,
  readOfflineMyOrdersProfile,
  readOfflineRoutesSnapshot,
  saveOfflineMyOrdersList,
  saveOfflineMyOrdersProfile,
  shouldUseOfflineDetailFallback
} from '../utils/offlineRoutesCache';

const MY_ORDERS_PAGE_SIZE = 25;

const buildMyOrdersListQuery = ({ stage, tab, offset = 0, search = '' }) => {
  const params = new URLSearchParams({
    department_scope: 'true',
    limit: String(MY_ORDERS_PAGE_SIZE),
    skip: String(offset),
    sort_mode: 'my_orders',
    my_orders_scope: tab,
  });

  if (tab === 'archive') {
    params.set('completed_stage', stage);
    params.set('completed_by_me', 'true');
  } else {
    params.set('status', stage);
  }
  if (search) {
    params.set('my_orders_search', search);
  }
  return params.toString();
};

const mergeOrdersById = (currentOrders, nextOrders) => {
  const merged = [...currentOrders];
  const knownIds = new Set(currentOrders.map((order) => order.id));
  nextOrders.forEach((order) => {
    if (!knownIds.has(order.id)) {
      knownIds.add(order.id);
      merged.push(order);
    }
  });
  return merged;
};

const getYandexAddressUrl = (address) => (
  address ? `https://yandex.ru/maps/?text=${encodeURIComponent(address)}` : null
);

const isCompanyOrder = (order) => (
  ['b2b', 'company', 'organization'].includes(order?.client_type)
);

const hasDispatcherPriorityForStage = (order, selectedStage) => {
  if (!order || order.priority === 'urgent') return false;
  if (order.dispatcher_priority_scope === 'order') return true;
  return order.dispatcher_priority_scope === 'stage' && order.dispatcher_priority_stage === selectedStage;
};

const hasDispatcherCallRequiredForStage = (order, selectedStage) => (
  !!order
  && ['pickup', 'delivery'].includes(selectedStage)
  && order.dispatcher_call_required_stage === selectedStage
);

const shortenDisplayAddress = (address = '') => {
  const value = String(address || '').trim();
  const parts = value.split(',').map(part => part.trim()).filter(Boolean);
  const cityIndex = parts.findIndex(part => /^г\.?\s/i.test(part));
  if (cityIndex > 0 && parts.slice(0, cityIndex).some(part => /обл\.?|область/i.test(part))) {
    return parts.slice(cityIndex).join(', ');
  }
  return value;
};

const normalizeOrderSearchText = (value = '') => (
  String(value || '')
    .trim()
    .toLocaleLowerCase('ru-RU')
    .replace(/ё/g, 'е')
);

const getRouteStatusBadge = (order) => {
  const lifecycleStatus = String(order?.lifecycle_status || '').trim() || (
    order?.has_active_revision ? 'revision_active' : (order?.status === 'completed' ? 'completed' : (order?.status === 'cancelled' ? 'cancelled' : 'active'))
  );
  if (lifecycleStatus === 'cancelled') {
    return {
      label: 'Отменен',
      dotClass: 'bg-red-500',
      stripeClass: 'bg-red-500',
      badgeClass: 'bg-red-50 ring-red-100 dark:bg-red-900/20 dark:ring-red-900/40',
      orderBadgeClass: 'bg-red-100 text-red-700 ring-red-200 dark:bg-red-900/30 dark:text-red-300 dark:ring-red-900/40'
    };
  }
  if (lifecycleStatus === 'revision_active') {
    return {
      label: 'Повтор',
      dotClass: 'bg-orange-500',
      stripeClass: 'bg-orange-500',
      badgeClass: 'bg-orange-50 ring-orange-100 dark:bg-orange-900/20 dark:ring-orange-900/40',
      orderBadgeClass: 'bg-orange-100 text-orange-700 ring-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:ring-orange-900/40'
    };
  }
  if (lifecycleStatus === 'completed') {
    return {
      label: 'Выполнен',
      dotClass: 'bg-green-500',
      stripeClass: 'bg-green-500',
      badgeClass: 'bg-green-50 ring-green-100 dark:bg-green-900/20 dark:ring-green-900/40',
      orderBadgeClass: 'bg-green-100 text-green-700 ring-green-200 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-900/40'
    };
  }
  if (lifecycleStatus === 'unassigned') {
    return {
      label: 'Нераспределен',
      dotClass: 'bg-gray-500',
      stripeClass: 'bg-gray-500',
      badgeClass: 'bg-gray-50 ring-gray-200 dark:bg-gray-900/20 dark:ring-gray-900/40',
      orderBadgeClass: 'bg-gray-100 text-gray-700 ring-gray-300 dark:bg-gray-900/30 dark:text-gray-300 dark:ring-gray-900/40'
    };
  }
  return {
    label: 'Выполняется',
    dotClass: 'bg-blue-500',
    stripeClass: 'bg-blue-500',
    badgeClass: 'bg-blue-50 ring-blue-100 dark:bg-blue-900/20 dark:ring-blue-900/40',
    orderBadgeClass: 'bg-blue-100 text-blue-700 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-900/40'
  };
};

const getDeliveryAddress = (order) => (
  order.revision_return_address_text || order.delivery_address_text || order.pickup_address_text || ''
);

const getPickupAddress = (order) => (
  order.revision_pickup_address_text || order.pickup_address_text || order.delivery_address_text || ''
);

const getRepeatPickupDate = (order) => order.revision_pickup_date || null;
const getRepeatPickupTimeSlot = (order) => order.revision_pickup_time_slot || null;
const getRepeatReturnDate = (order) => order.revision_return_date || null;
const getRepeatReturnTimeSlot = (order) => order.revision_return_time_slot || null;

const isRepeatLifecycle = (order) => String(order?.lifecycle_status || '').trim() === 'revision_active';

const getTimeStartMinutes = (timeSlot = '') => {
  const match = String(timeSlot).match(/(\d{1,2})[:.](\d{2})/);
  if (!match) return 24 * 60;
  return Number(match[1]) * 60 + Number(match[2]);
};

const formatTimeSlot = (timeSlot = '') => (
  String(timeSlot || '').trim().replace(/\s*-\s*/g, ' - ')
);

const getStageSchedule = (order, stage) => {
  if (stage === 'pickup') {
    return {
      date: isRepeatLifecycle(order) ? (getRepeatPickupDate(order) || order.pickup_date) : order.pickup_date,
      time: isRepeatLifecycle(order) ? (getRepeatPickupTimeSlot(order) || order.pickup_time_slot) : order.pickup_time_slot
    };
  }

  return {
    date: isRepeatLifecycle(order) && order.revision_return_delivery_type === 'delivery'
      ? getRepeatReturnDate(order)
      : order.delivery_date,
    time: isRepeatLifecycle(order) && order.revision_return_delivery_type === 'delivery'
      ? getRepeatReturnTimeSlot(order)
      : order.delivery_time_slot
  };
};

const getStageDateTimeParts = (order, stage) => {
  const schedule = getStageSchedule(order, stage);
  return {
    dateText: schedule.date ? formatTimezoneDate(schedule.date, false) : 'Дата не указана',
    timeText: formatTimeSlot(schedule.time) || 'Время не указано'
  };
};

const getStageOverdueMeta = (order, stage) => {
  const schedule = getStageSchedule(order, stage);
  if (!schedule.date) {
    return { dateOverdue: false, timeOverdue: false };
  }

  const now = new Date();
  const scheduleDate = new Date(String(schedule.date).endsWith('Z') ? schedule.date : `${schedule.date}Z`);
  if (Number.isNaN(scheduleDate.getTime())) {
    return { dateOverdue: false, timeOverdue: false };
  }

  const dayStart = new Date(scheduleDate);
  dayStart.setUTCHours(0, 0, 0, 0);

  const nowDayStart = new Date(now);
  nowDayStart.setHours(0, 0, 0, 0);

  const dateOverdue = dayStart.getTime() < nowDayStart.getTime();
  const isSameDay = dayStart.getUTCFullYear() === now.getFullYear()
    && dayStart.getUTCMonth() === now.getMonth()
    && dayStart.getUTCDate() === now.getDate();

  const timeOverdue = isSameDay && !!schedule.time && getTimeStartMinutes(schedule.time) < (now.getHours() * 60 + now.getMinutes());

  return { dateOverdue, timeOverdue };
};

const getEffectivePickupDate = (order) => order.pickup_completed_date || getRepeatPickupDate(order) || order.pickup_date || null;
const getEffectiveDeliveryDate = (order) => (
  order.delivery_completed_date
  || (isRepeatLifecycle(order) ? getRepeatReturnDate(order) : order.delivery_date)
  || null
);

const getStageCardAddress = (order, stage) => (
  stage === 'delivery' ? getDeliveryAddress(order) : getPickupAddress(order)
);

const hasSavedApprovalReturnConditions = (order) => {
  const returnType = isRepeatLifecycle(order) ? (order.revision_return_delivery_type || '') : order.return_delivery_type;
  if (!returnType) return false;
  if (returnType === 'self_pickup') return true;
  const deliveryDate = isRepeatLifecycle(order) && returnType === 'delivery'
    ? order.revision_return_date
    : order.delivery_date;
  const deliveryTime = isRepeatLifecycle(order) && returnType === 'delivery'
    ? order.revision_return_time_slot
    : order.delivery_time_slot;
  const deliveryAddress = isRepeatLifecycle(order) && returnType === 'delivery'
    ? (order.revision_return_address_text || order.delivery_address_text)
    : order.delivery_address_text;

  return !!(deliveryDate && deliveryTime && deliveryAddress);
};

const ARCHIVE_STAGE_BADGE_STYLES = {
  default: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
};

const getArchiveStageBadgeMeta = (status) => {
  if (status === 'completed') {
    return {
      label: 'Завершен',
      className: ARCHIVE_STAGE_BADGE_STYLES.default,
      icon: Check,
      iconClassName: 'text-green-600 dark:text-green-400'
    };
  }

  if (status === 'cancelled') {
    return {
      label: 'Отменен',
      className: ARCHIVE_STAGE_BADGE_STYLES.default,
      icon: XCircle,
      iconClassName: 'text-red-600 dark:text-red-400'
    };
  }

  return {
    label: STAGES_CONFIG[status]?.label || 'Следующий этап',
    className: ARCHIVE_STAGE_BADGE_STYLES.default,
    icon: STAGES_CONFIG[status]?.icon || Package,
    iconClassName:
      STAGES_CONFIG[status]?.color === 'amber' ? 'text-amber-600 dark:text-amber-400'
        : STAGES_CONFIG[status]?.color === 'orange' ? 'text-orange-600 dark:text-orange-400'
        : STAGES_CONFIG[status]?.color === 'rose' ? 'text-rose-600 dark:text-rose-400'
        : STAGES_CONFIG[status]?.color === 'teal' ? 'text-teal-600 dark:text-teal-400'
          : STAGES_CONFIG[status]?.color === 'lime' ? 'text-lime-600 dark:text-lime-400'
            : STAGES_CONFIG[status]?.color === 'indigo' ? 'text-indigo-600 dark:text-indigo-400'
              : STAGES_CONFIG[status]?.color === 'violet' ? 'text-violet-600 dark:text-violet-400'
                : STAGES_CONFIG[status]?.color === 'fuchsia' ? 'text-fuchsia-600 dark:text-fuchsia-400'
        : STAGES_CONFIG[status]?.color === 'green' ? 'text-green-600 dark:text-green-400'
          : STAGES_CONFIG[status]?.color === 'blue' ? 'text-blue-600 dark:text-blue-400'
            : STAGES_CONFIG[status]?.color === 'yellow' ? 'text-amber-600 dark:text-amber-400'
              : STAGES_CONFIG[status]?.color === 'purple' ? 'text-violet-600 dark:text-violet-400'
                : STAGES_CONFIG[status]?.color === 'red' ? 'text-red-600 dark:text-red-400'
                  : STAGES_CONFIG[status]?.color === 'cyan' ? 'text-cyan-600 dark:text-cyan-400'
                    : STAGES_CONFIG[status]?.color === 'emerald' ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-gray-500 dark:text-gray-400'
  };
};

const getMyOrdersRouteState = (search = '') => {
  const params = new URLSearchParams(search);
  const urlStage = params.get('stage');
  const urlTab = params.get('tab');

  return {
    stage: urlStage || null,
    tab: urlStage && ['my', 'available', 'archive'].includes(urlTab) ? urlTab : 'my'
  };
};

const getStoredCurrentUserId = () => {
  if (typeof localStorage === 'undefined') return null;
  const userId = Number(localStorage.getItem('user_id'));
  return Number.isFinite(userId) && userId > 0 ? userId : null;
};

const normalizeId = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const getOrderFlowForStage = (order, stage) => (
  (Array.isArray(order?.washing_flow_summaries) ? order.washing_flow_summaries : [])
    .find((flow) => String(flow?.stage_name || '') === String(stage || ''))
);

const isOrderAssignedToUser = (order, userId, stage) => {
  const normalizedUserId = normalizeId(userId);
  if (!normalizedUserId) return false;

  const flow = getOrderFlowForStage(order, stage);
  const assignedTo = flow
    ? (flow.assigned_to ?? (flow.stage_status === 'completed' ? flow.completed_by : null))
    : order?.assigned_to;
  return normalizeId(assignedTo) === normalizedUserId;
};

const isOrderAvailableForStage = (order, stage) => {
  const flow = getOrderFlowForStage(order, stage);
  if (flow?.stage_status === 'completed') return false;
  const assignedTo = flow ? flow.assigned_to : order?.assigned_to;
  return normalizeId(assignedTo) === null;
};

const getOfflineRouteStagesForUser = (snapshot, userId) => {
  const normalizedUserId = normalizeId(userId);
  if (!normalizedUserId) return [];

  const stages = new Set();
  (snapshot?.items || []).forEach((item) => {
    if (item?.entity_type === 'task') return;
    if (normalizeId(item?.assigned_to) !== normalizedUserId) return;
    const stageName = String(item?.stage_name || '').trim();
    if (stageName) {
      stages.add(stageName);
    }
  });

  return USER_STAGE_ORDER.filter((stageName) => stages.has(stageName));
};

const buildOfflineMyOrdersFromRouteSnapshot = (snapshot, stage, userId) => {
  const normalizedUserId = normalizeId(userId);
  const selectedStage = String(stage || '').trim();
  if (!snapshot || !normalizedUserId || !selectedStage) return [];

  return (snapshot.items || [])
    .filter((item) => item?.entity_type !== 'task')
    .filter((item) => String(item?.stage_name || '') === selectedStage)
    .filter((item) => normalizeId(item?.assigned_to) === normalizedUserId)
    .map((item) => {
      const isDeliveryStage = selectedStage === 'delivery' || selectedStage === 'handover';
      const scheduledDate = item?.scheduled_date || null;
      const itemsCount = Number(item?.items_count || 0)
        || (Array.isArray(item?.items_summary) ? item.items_summary.length : 0);

      return {
        id: Number(item?.order_id) || item?.order_id,
        order_number: item?.order_number || String(item?.order_id || ''),
        client_id: 0,
        client_type: item?.client_type || 'b2c',
        source: null,
        client_name: item?.client_name || '',
        client_phone: item?.client_phone || '',
        client_vip: false,
        service_type: item?.service_type || (selectedStage === 'cleaning' ? 'cleaning' : 'washing'),
        status: selectedStage,
        payment_status: item?.payment_status || 'pending',
        priority: 'normal',
        dispatcher_priority_scope: null,
        dispatcher_priority_stage: null,
        active_rework: false,
        rework_items_count: 0,
        dispatcher_call_required_stage: item?.dispatcher_call_required_stage || null,
        assigned_to: item?.assigned_to ?? null,
        assignee_name: item?.assignee_name || '',
        department_name: item?.department_name || null,
        self_delivery: false,
        return_delivery_type: isDeliveryStage ? 'delivery' : null,
        final_amount: Number(item?.final_amount || 0),
        items_count: itemsCount,
        stage_completed_items_count: 0,
        area_required_count: 0,
        area_filled_count: 0,
        weight_required_count: 0,
        weight_filled_count: 0,
        pickup_date: isDeliveryStage ? null : scheduledDate,
        pickup_completed_date: null,
        pickup_time_slot: isDeliveryStage ? '' : (item?.time_slot || ''),
        delivery_date: isDeliveryStage ? scheduledDate : null,
        delivery_completed_date: null,
        handover_completed_date: null,
        delivery_time_slot: isDeliveryStage ? (item?.time_slot || '') : '',
        pickup_address_text: isDeliveryStage ? '' : (item?.address_text || ''),
        delivery_address_text: isDeliveryStage ? (item?.address_text || '') : '',
        revision_pickup_date: null,
        revision_pickup_time_slot: null,
        revision_pickup_address_text: null,
        revision_return_delivery_type: null,
        revision_return_date: null,
        revision_return_time_slot: null,
        revision_return_address_text: null,
        unread_comments_count: 0,
        has_unread_comments: false,
        has_active_revision: Boolean(item?.has_active_revision),
        lifecycle_status: item?.lifecycle_status || 'active',
        lifecycle_label: item?.lifecycle_label || 'Выполняется',
        washing_flow_summaries: [],
        created_date: item?.updated_at || scheduledDate || snapshot?.syncedAt || new Date().toISOString(),
        completed_date: null,
      };
    });
};

const mergeOfflineOrderLists = (primaryOrders = [], fallbackOrders = []) => {
  const byId = new Map();
  [...fallbackOrders, ...primaryOrders].forEach((order) => {
    const key = String(order?.id || '');
    if (key) {
      byId.set(key, order);
    }
  });
  return Array.from(byId.values());
};

const MyOrdersListSkeleton = ({ count = 4 }) => (
  <div className="space-y-2">
    {[...Array(count)].map((_, index) => (
      <div
        key={`my-orders-skeleton-${index}`}
        className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900"
      >
        <div className="space-y-3 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-5 w-28 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="h-4 w-2/3 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
            </div>
            <div className="h-6 w-6 shrink-0 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="h-3.5 w-3.5 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="h-3 w-24 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="h-3 w-16 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
            </div>
            <div className="flex items-start gap-2">
              <div className="mt-0.5 h-3.5 w-3.5 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="h-3 w-3/4 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="h-3 w-1/2 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3.5 w-3.5 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="h-3 w-32 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
            </div>
          </div>
        </div>

        <div className="px-4 pb-3 pt-0">
          <div className="h-[34px] w-full animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
        </div>
      </div>
    ))}
  </div>
);

const MyOrdersStagesSkeleton = ({ count = 4 }) => (
  <div className="space-y-6">
    <div className="rounded-2xl bg-white p-6 shadow-sm dark:bg-gray-900">
      <div className="mb-4 h-4 w-32 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />

      <div className="space-y-3">
        {[...Array(count)].map((_, index) => (
          <div
            key={`my-orders-stage-skeleton-${index}`}
            className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
          >
            <div className="h-9 w-9 shrink-0 animate-pulse rounded-md bg-gray-200 dark:bg-gray-700" />
            <div className="h-4 w-40 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const STAGE_SELECTOR_STYLES = {
  amber: {
    row: 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600 dark:hover:bg-gray-800',
    stripe: 'from-amber-500 to-amber-400 dark:from-amber-400 dark:to-amber-300',
    icon: 'bg-amber-500 text-white shadow-[0_10px_24px_rgba(245,158,11,0.28)]',
    text: 'text-gray-900 dark:text-gray-100',
    arrow: 'text-amber-500 dark:text-amber-300'
  },
  cyan: {
    row: 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600 dark:hover:bg-gray-800',
    stripe: 'from-cyan-500 to-cyan-400 dark:from-cyan-400 dark:to-cyan-300',
    icon: 'bg-cyan-500 text-white shadow-[0_10px_24px_rgba(6,182,212,0.28)]',
    text: 'text-gray-900 dark:text-gray-100',
    arrow: 'text-cyan-500 dark:text-cyan-300'
  },
  rose: {
    row: 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600 dark:hover:bg-gray-800',
    stripe: 'from-rose-500 to-rose-400 dark:from-rose-400 dark:to-rose-300',
    icon: 'bg-rose-500 text-white shadow-[0_10px_24px_rgba(244,63,94,0.28)]',
    text: 'text-gray-900 dark:text-gray-100',
    arrow: 'text-rose-500 dark:text-rose-300'
  },
  indigo: {
    row: 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600 dark:hover:bg-gray-800',
    stripe: 'from-indigo-500 to-indigo-400 dark:from-indigo-400 dark:to-indigo-300',
    icon: 'bg-indigo-500 text-white shadow-[0_10px_24px_rgba(99,102,241,0.28)]',
    text: 'text-gray-900 dark:text-gray-100',
    arrow: 'text-indigo-500 dark:text-indigo-300'
  },
  purple: {
    row: 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600 dark:hover:bg-gray-800',
    stripe: 'from-purple-500 to-purple-400 dark:from-purple-400 dark:to-purple-300',
    icon: 'bg-purple-500 text-white shadow-[0_10px_24px_rgba(168,85,247,0.28)]',
    text: 'text-gray-900 dark:text-gray-100',
    arrow: 'text-purple-500 dark:text-purple-300'
  },
  yellow: {
    row: 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600 dark:hover:bg-gray-800',
    stripe: 'from-yellow-500 to-yellow-400 dark:from-yellow-400 dark:to-yellow-300',
    icon: 'bg-yellow-500 text-white shadow-[0_10px_24px_rgba(234,179,8,0.28)]',
    text: 'text-gray-900 dark:text-gray-100',
    arrow: 'text-yellow-500 dark:text-yellow-300'
  },
  teal: {
    row: 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600 dark:hover:bg-gray-800',
    stripe: 'from-teal-500 to-teal-400 dark:from-teal-400 dark:to-teal-300',
    icon: 'bg-teal-500 text-white shadow-[0_10px_24px_rgba(20,184,166,0.28)]',
    text: 'text-gray-900 dark:text-gray-100',
    arrow: 'text-teal-500 dark:text-teal-300'
  },
  lime: {
    row: 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600 dark:hover:bg-gray-800',
    stripe: 'from-lime-500 to-lime-400 dark:from-lime-400 dark:to-lime-300',
    icon: 'bg-lime-500 text-white shadow-[0_10px_24px_rgba(132,204,22,0.28)]',
    text: 'text-gray-900 dark:text-gray-100',
    arrow: 'text-lime-500 dark:text-lime-300'
  },
  fuchsia: {
    row: 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600 dark:hover:bg-gray-800',
    stripe: 'from-fuchsia-500 to-fuchsia-400 dark:from-fuchsia-400 dark:to-fuchsia-300',
    icon: 'bg-fuchsia-500 text-white shadow-[0_10px_24px_rgba(217,70,239,0.28)]',
    text: 'text-gray-900 dark:text-gray-100',
    arrow: 'text-fuchsia-500 dark:text-fuchsia-300'
  },
  green: {
    row: 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600 dark:hover:bg-gray-800',
    stripe: 'from-green-500 to-green-400 dark:from-green-400 dark:to-green-300',
    icon: 'bg-green-500 text-white shadow-[0_10px_24px_rgba(34,197,94,0.28)]',
    text: 'text-gray-900 dark:text-gray-100',
    arrow: 'text-green-500 dark:text-green-300'
  },
  gray: {
    row: 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100/80 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 dark:hover:bg-gray-700',
    stripe: 'from-gray-500 to-gray-400 dark:from-gray-400 dark:to-gray-300',
    icon: 'bg-gray-500 text-white shadow-[0_10px_24px_rgba(107,114,128,0.24)]',
    text: 'text-gray-950 dark:text-gray-50',
    arrow: 'text-gray-400 dark:text-gray-500'
  }
};

const MyOrders = () => {
  const offline = useOffline();
  const connectionUnavailable = ['offline', 'server_unavailable'].includes(offline?.networkStatus);
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = useMemo(() => getMyOrdersRouteState(location.search), [location.search]);
  const isDryCleaningEnabled = useDryCleaningEnabled();
  const selectedStage = routeState.stage;
  const isDeferredStage = selectedStage === 'deferred';
  const activeTab = routeState.tab; // 'my', 'available' или 'archive'
  const [orders, setOrders] = useState([]);
  const [tabCounts, setTabCounts] = useState({ my: null, available: null, archive: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [stagesLoading, setStagesLoading] = useState(() => {
    const storedRoles = localStorage.getItem('user_roles');
    const storedUserId = getStoredCurrentUserId();
    const roles = getStagesFromRoles(storedRoles || '[]');
    if (storedUserId && roles.length > 0) return false;
    const snapshotRoles = Array.isArray(offline?.snapshot?.stageRoles) ? offline.snapshot.stageRoles : [];
    if (storedUserId && snapshotRoles.length > 0) return false;
    return true;
  });
  const [userId, setUserId] = useState(() => getStoredCurrentUserId());
  const [userRoles, setUserRoles] = useState(() => {
    const storedRoles = localStorage.getItem('user_roles');
    const roles = getStagesFromRoles(storedRoles || '[]');
    if (roles.length > 0) return roles;
    const snapshotRoles = Array.isArray(offline?.snapshot?.stageRoles) ? offline.snapshot.stageRoles : [];
    if (snapshotRoles.length > 0) return snapshotRoles;
    return [];
  });
  const [currentUserType, setCurrentUserType] = useState(() => {
    return String(localStorage.getItem('user_type') || '').trim().toLowerCase();
  });
  const [canAccessDeferredOrders, setCanAccessDeferredOrders] = useState(false);
  const [canAccessTasks, setCanAccessTasks] = useState(false);
  const [usingOfflineMyOrders, setUsingOfflineMyOrders] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null); // { orderId, orderNumber }
  const [mapConfirmModal, setMapConfirmModal] = useState(null); // { address, url }
  const { toast, showToast, hideToast } = useToast(3000);
  const [assigning, setAssigning] = useState(false);
  const { animationsEnabled } = useInteractionPreferences();
  const strictBackToStageRef = useRef(sessionStorage.getItem('myOrdersBackStageStrict') === '1');
  const loadMoreSentinelRef = useRef(null);
  const loadedListKeyRef = useRef('');
  const canViewDeferredOrders = ['dispatcher', 'admin', 'developer'].includes(currentUserType)
    && canAccessDeferredOrders;

  useModalBackButton(!!confirmModal, () => setConfirmModal(null));
  useModalBackButton(!!mapConfirmModal, () => setMapConfirmModal(null));
  const { updateStage, selectedStage: contextSelectedStage, isStageSelected: contextIsStageSelected } = useStageContext();

  useEffect(() => {
    if (userRoles.length === 0 && Array.isArray(offline?.snapshot?.stageRoles) && offline.snapshot.stageRoles.length > 0) {
      setUserRoles(offline.snapshot.stageRoles);
      setStagesLoading(false);
    }
  }, [offline?.snapshot?.stageRoles, userRoles.length]);

  useEffect(() => {
    const nextStage = selectedStage || null;
    if (contextSelectedStage === nextStage && contextIsStageSelected === !!nextStage) {
      return;
    }

    updateStage(nextStage);

    if (nextStage) {
      sessionStorage.setItem('myOrdersStage', nextStage);
    }
  }, [selectedStage, contextSelectedStage, contextIsStageSelected, updateStage]);

  // Функция для выбора этапа
  useEffect(() => {
    if (!isDryCleaningEnabled && selectedStage === 'cleaning') {
      navigate('/my-orders', { replace: true });
      updateStage(null);
    }
  }, [isDryCleaningEnabled, selectedStage, navigate, updateStage]);

  const handleStageSelect = (stage) => {
    if (!stage) return;
    haptic('selection');
    sessionStorage.setItem('myOrdersStage', stage);
    sessionStorage.setItem('myOrdersActiveTab', 'my');
    navigate(`/my-orders?${new URLSearchParams({ tab: 'my', stage }).toString()}`, { replace: true });
  };

  const handleTabChange = (tab) => {
    if (!selectedStage || tab === activeTab) return;
    haptic('selection');
    sessionStorage.setItem('myOrdersActiveTab', tab);
    navigate(`/my-orders?${new URLSearchParams({ tab, stage: selectedStage }).toString()}`, { replace: true });
  };

  // Загрузка данных пользователя
  useEffect(() => {
    let disposed = false;

    const loadUser = async () => {
      const applyUserContext = (nextUserId, stageRoles, nextUserType = '', nextDeferredAccess = false, nextAvailableMenus = '[]') => {
        if (disposed) return;
        const normalizedUserType = String(nextUserType || '').trim().toLowerCase();
        const hasPrivilegedDeferredRole = ['dispatcher', 'admin', 'developer'].includes(normalizedUserType);
        const hasDeferredQueue = hasPrivilegedDeferredRole && Boolean(nextDeferredAccess);
        let menuIds = [];
        try {
          menuIds = Array.isArray(nextAvailableMenus) ? nextAvailableMenus : JSON.parse(nextAvailableMenus || '[]');
        } catch {
          menuIds = [];
        }
        setUserId(nextUserId);
        setUserRoles(stageRoles);
        setCurrentUserType(normalizedUserType);
        setCanAccessDeferredOrders(hasDeferredQueue);
        setCanAccessTasks(Array.isArray(menuIds) && menuIds.includes('tasks'));

        if (selectedStage) {
          sessionStorage.removeItem('myOrdersBackStageStrict');
          strictBackToStageRef.current = false;
          return;
        }

        const savedStage = sessionStorage.getItem('myOrdersStage');
        if (savedStage) {
          if (stageRoles.includes(savedStage)) {
            navigate(`/my-orders?${new URLSearchParams({ tab: 'my', stage: savedStage }).toString()}`, { replace: true });
          } else {
            sessionStorage.removeItem('myOrdersStage');
            updateStage(null);
          }
          sessionStorage.removeItem('myOrdersBackStageStrict');
          strictBackToStageRef.current = false;
          return;
        }

        // Если только один этап доступен - открываем его сразу,
        // кроме строгого возврата из уведомления, где нужен экран выбора этапа.
        if (stageRoles.length === 1 && !hasDeferredQueue && !strictBackToStageRef.current) {
          const onlyStage = stageRoles[0];
          sessionStorage.setItem('myOrdersStage', onlyStage);
          navigate(`/my-orders?${new URLSearchParams({ tab: 'my', stage: onlyStage }).toString()}`, { replace: true });
        }
      };

      // 1. Сразу применяем сохранённый профиль, чтобы не блокировать интерфейс скелетоном
      const storedInitUserId = getStoredCurrentUserId();
      const storedInitRoles = localStorage.getItem('user_roles');
      let initialStageRoles = getStagesFromRoles(storedInitRoles || '[]');
      if (!initialStageRoles.length && Array.isArray(offline?.snapshot?.stageRoles) && offline.snapshot.stageRoles.length) {
        initialStageRoles = offline.snapshot.stageRoles;
      }

      if (storedInitUserId && initialStageRoles.length > 0) {
        applyUserContext(
          storedInitUserId,
          initialStageRoles,
          localStorage.getItem('user_type') || '',
          false,
          localStorage.getItem('available_menus') || '[]'
        );
        setStagesLoading(false);
      } else {
        try {
          const cachedProfile = await readOfflineMyOrdersProfile(storedInitUserId);
          if (cachedProfile?.stageRoles?.length) {
            applyUserContext(
              cachedProfile.userId,
              cachedProfile.stageRoles,
              localStorage.getItem('user_type') || '',
              false,
              localStorage.getItem('available_menus') || '[]'
            );
            setStagesLoading(false);
          } else {
            const routeSnapshot = await readOfflineRoutesSnapshot(storedInitUserId).catch(() => null);
            const routeStages = routeSnapshot?.stageRoles?.length
              ? routeSnapshot.stageRoles
              : getOfflineRouteStagesForUser(routeSnapshot, storedInitUserId);
            if (storedInitUserId && routeStages?.length > 0) {
              applyUserContext(
                storedInitUserId,
                routeStages,
                localStorage.getItem('user_type') || '',
                false,
                localStorage.getItem('available_menus') || '[]'
              );
              setStagesLoading(false);
            } else {
              setStagesLoading(true);
            }
          }
        } catch {
          setStagesLoading(true);
        }
      }

      // 2. Если сеть недоступна (offline или server_unavailable / "пытаюсь соединиться") — НЕ отправляем fetch, который зависнет!
      if (connectionUnavailable || navigator.onLine === false) {
        if (!disposed) setStagesLoading(false);
        return;
      }

      // 3. Если сеть активна, запрашиваем check-auth С ТАЙМАУТОМ (3.5 секунды)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3500);

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/check-auth`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            ...getClientVersionHeaders()
          },
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          const error = new Error(`Не удалось загрузить пользователя (${response.status})`);
          error.status = response.status;
          throw error;
        }

        const data = await response.json();
        const stageRoles = getStagesFromRoles(data.user_roles || '[]');
        if (data.user_roles) {
          localStorage.setItem('user_roles', typeof data.user_roles === 'string' ? data.user_roles : JSON.stringify(data.user_roles));
        }
        await saveOfflineMyOrdersProfile({
          userId: data.user_id,
          stageRoles
        }).catch((cacheError) => {
          console.warn('Не удалось сохранить офлайн-профиль моих заказов:', cacheError);
        });
        applyUserContext(
          data.user_id,
          stageRoles,
          data.user_type,
          data.can_access_deferred_orders !== false,
          data.available_menus,
        );
      } catch (error) {
        clearTimeout(timeoutId);
        console.warn('Проверка пользователя через сеть не удалась, используем офлайн-данные:', error);
        try {
          const cachedProfile = await readOfflineMyOrdersProfile(getStoredCurrentUserId());
          if (cachedProfile?.stageRoles?.length) {
            applyUserContext(
              cachedProfile.userId,
              cachedProfile.stageRoles,
              localStorage.getItem('user_type') || '',
              false,
              localStorage.getItem('available_menus') || '[]'
            );
            return;
          }

          const storedUserId = getStoredCurrentUserId();
          const savedStage = sessionStorage.getItem('myOrdersStage');
          const routeSnapshot = await readOfflineRoutesSnapshot(storedUserId).catch(() => null);
          const routeStages = routeSnapshot?.stageRoles?.length
            ? routeSnapshot.stageRoles
            : getOfflineRouteStagesForUser(routeSnapshot, storedUserId);
          if (storedUserId && routeStages.length > 0) {
            applyUserContext(storedUserId, routeStages);
            return;
          }

          if (storedUserId && (selectedStage || savedStage)) {
            const fallbackStages = [selectedStage || savedStage].filter(Boolean);
            setUserId(storedUserId);
            setUserRoles(fallbackStages);
            if (!selectedStage && savedStage) {
              navigate(`/my-orders?${new URLSearchParams({ tab: 'my', stage: savedStage }).toString()}`, { replace: true });
            }
          }
        } catch (cacheError) {
          console.warn('Не удалось прочитать офлайн-профиль моих заказов:', cacheError);
        }
      } finally {
        if (!disposed) setStagesLoading(false);
      }
    };

    loadUser();
    return () => { disposed = true; };
  }, [navigate, selectedStage, updateStage, connectionUnavailable, offline?.snapshot?.stageRoles]);

  useEffect(() => {
    sessionStorage.setItem('myOrdersActiveTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
  }, [selectedStage, activeTab]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const normalizedDebouncedSearch = useMemo(
    () => normalizeOrderSearchText(debouncedSearchQuery).replace(/^№\s*/, ''),
    [debouncedSearchQuery]
  );
  const serverSearchQuery = useMemo(
    () => String(debouncedSearchQuery || '').trim().replace(/^№\s*/i, ''),
    [debouncedSearchQuery]
  );
  const listKey = `${selectedStage || ''}:${activeTab}:${normalizedDebouncedSearch}`;

  const requestOrdersPage = useCallback(async (token, offset = 0) => {
    if (connectionUnavailable || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
      throw new Error('Нет соединения с сервером');
    }
    const query = buildMyOrdersListQuery({
      stage: selectedStage,
      tab: activeTab,
      offset,
      search: serverSearchQuery,
    });
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4000);
    try {
      const response = await fetch(`${API_URL}/api/orders?${query}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (!response.ok) {
        const error = new Error(`Не удалось загрузить заказы (${response.status})`);
        error.status = response.status;
        throw error;
      }
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (err) {
      clearTimeout(timeoutId);
      throw err;
    }
  }, [activeTab, selectedStage, serverSearchQuery, connectionUnavailable]);

  const requestTabCounts = useCallback(async (token) => {
    if (!selectedStage || isDeferredStage || connectionUnavailable || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
      return null;
    }
    const params = new URLSearchParams({ stage_name: selectedStage });
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4000);
    try {
      const response = await fetch(`${API_URL}/api/orders/my-orders-counts?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (!response.ok) {
        throw new Error(`Не удалось загрузить количества вкладок (${response.status})`);
      }
      return response.json();
    } catch (err) {
      clearTimeout(timeoutId);
      throw err;
    }
  }, [connectionUnavailable, isDeferredStage, selectedStage]);

  // Загрузка первой страницы заказов
  useEffect(() => {
    if (!selectedStage || !userId || isDeferredStage) return;

    let disposed = false;
    loadedListKeyRef.current = listKey;

    const readCachedOrdersForView = async () => {
      try {
        const cachedList = await readOfflineMyOrdersList({ userId, stage: selectedStage, tab: activeTab });
        const cachedOrders = Array.isArray(cachedList?.orders) ? cachedList.orders : [];
        let routeOrders = [];
        if (activeTab === 'my') {
          const routeSnapshot = await readOfflineRoutesSnapshot(userId).catch(() => null);
          routeOrders = buildOfflineMyOrdersFromRouteSnapshot(routeSnapshot, selectedStage, userId);
        }
        if (!cachedList && routeOrders.length === 0) return null;
        const nextOfflineOrders = mergeOfflineOrderLists(cachedOrders, routeOrders)
          .filter((order) => {
            if (activeTab === 'my') {
              return isOrderAssignedToUser(order, userId, selectedStage)
                && order.status !== 'completed' && order.status !== 'cancelled';
            }
            if (activeTab === 'available') {
              return isOrderAvailableForStage(order, selectedStage)
                && order.status !== 'completed' && order.status !== 'cancelled';
            }
            return true;
          })
          .filter((order) => {
            if (!normalizedDebouncedSearch) return true;
            const orderNumber = normalizeOrderSearchText(order?.order_number).replace(/^№\s*/, '');
            const address = normalizeOrderSearchText(getStageCardAddress(order, selectedStage));
            return orderNumber.includes(normalizedDebouncedSearch)
              || address.includes(normalizedDebouncedSearch);
          });
        return nextOfflineOrders;
      } catch (cacheError) {
        console.warn('Не удалось прочитать офлайн-список моих заказов:', cacheError);
        return null;
      }
    };

    const loadOrders = async () => {
      setLoadingMore(false);
      setHasMore(false);

      // Сначала мгновенно показываем офлайн-данные, если они сохранены (без скелетона)
      let hadCached = false;
      const cached = await readCachedOrdersForView();
      if (disposed) return;
      if (cached !== null) {
        setOrders(cached);
        setUsingOfflineMyOrders(true);
        setTabCounts((prev) => ({ ...prev, [activeTab]: cached.length }));
        hadCached = true;
      } else {
        // Скелетон включаем только если локальных данных совсем нет
        setLoading(true);
        setOrders([]);
      }

      if (connectionUnavailable || navigator.onLine === false) {
        if (!hadCached && !disposed) {
          setOrders([]);
          setUsingOfflineMyOrders(false);
        }
        if (!disposed) setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const data = await requestOrdersPage(token, 0);
        if (disposed) return;
        setOrders(data);
        setHasMore(data.length === MY_ORDERS_PAGE_SIZE);
        setUsingOfflineMyOrders(false);
        if (!normalizedDebouncedSearch) {
          saveOfflineMyOrdersList({
            userId,
            stage: selectedStage,
            tab: activeTab,
            orders: data
          }).catch((cacheError) => {
            console.warn('Не удалось сохранить офлайн-список моих заказов:', cacheError);
          });
        }
      } catch (error) {
        console.error('Ошибка загрузки заказов:', error);
        if (disposed) return;
        if (!hadCached) {
          if (shouldUseOfflineDetailFallback(error)) {
            const fallbackOrders = await readCachedOrdersForView();
            if (!disposed && fallbackOrders !== null) {
              setOrders(fallbackOrders);
              setHasMore(false);
              setUsingOfflineMyOrders(true);
              setTabCounts((previous) => ({ ...previous, [activeTab]: fallbackOrders.length }));
              return;
            }
          }
          setOrders([]);
          setUsingOfflineMyOrders(false);
        }
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    };

    loadOrders();
    return () => {
      disposed = true;
    };
  }, [activeTab, connectionUnavailable, isDeferredStage, listKey, normalizedDebouncedSearch, requestOrdersPage, selectedStage, userId]);

  // Фильтрация по вкладкам
  const filteredOrders = orders.filter(order => {
    if (activeTab === 'my') {
      // Мои заказы - где пользователь назначен исполнителем
      return isOrderAssignedToUser(order, userId, selectedStage) && order.status !== 'completed' && order.status !== 'cancelled';
    } else if (activeTab === 'available') {
      // Доступные - где нет исполнителя
      return isOrderAvailableForStage(order, selectedStage) && order.status !== 'completed' && order.status !== 'cancelled';
    } else if (activeTab === 'archive') {
      // Архив - заказы, где пользователь уже завершил выбранный этап
      return true;
    }
    return true;
  });

  const normalizedSearchQuery = normalizeOrderSearchText(searchQuery).replace(/^№\s*/, '');
  const displayedOrders = normalizedSearchQuery
    ? filteredOrders.filter((order) => {
      const orderNumber = normalizeOrderSearchText(order?.order_number).replace(/^№\s*/, '');
      const stageAddress = normalizeOrderSearchText(getStageCardAddress(order, selectedStage));
      return orderNumber.includes(normalizedSearchQuery) || stageAddress.includes(normalizedSearchQuery);
    })
    : filteredOrders;

  // Один компактный запрос вместо загрузки полных списков соседних вкладок.
  useEffect(() => {
    if (!selectedStage || !userId || isDeferredStage || connectionUnavailable || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
      return undefined;
    }

    let disposed = false;
    setTabCounts({ my: null, available: null, archive: null });
    requestTabCounts(localStorage.getItem('token')).then((counts) => {
      if (!disposed && counts) {
        setTabCounts(counts);
      }
    }).catch(() => {
      // Основной список уже обработает ошибку и покажет офлайн-данные при необходимости.
    });
    return () => {
      disposed = true;
    };
  }, [connectionUnavailable, isDeferredStage, requestTabCounts, selectedStage, userId]);

  useEffect(() => {
    const activeCount = tabCounts[activeTab];
    if (loading || normalizedDebouncedSearch || activeCount === null) return;
    setHasMore(orders.length < activeCount);
  }, [activeTab, loading, normalizedDebouncedSearch, orders.length, tabCounts]);

  const readOnlyOffline = connectionUnavailable || usingOfflineMyOrders || (typeof navigator !== 'undefined' && navigator.onLine === false);

  const reloadOrders = async (token) => {
    if (!selectedStage || !token || isDeferredStage) return;
    const data = await requestOrdersPage(token, 0);
    setOrders(data);
    setHasMore(data.length === MY_ORDERS_PAGE_SIZE);
    setUsingOfflineMyOrders(false);
    if (!normalizedDebouncedSearch) {
      saveOfflineMyOrdersList({
        userId,
        stage: selectedStage,
        tab: activeTab,
        orders: data
      }).catch((cacheError) => {
        console.warn('Не удалось сохранить офлайн-список моих заказов:', cacheError);
      });
    }
    requestTabCounts(token).then((counts) => {
      if (counts) setTabCounts(counts);
    }).catch(() => {});
  };

  const handleLoadMore = useCallback(async () => {
    if (!selectedStage || isDeferredStage || loading || loadingMore || !hasMore || readOnlyOffline) return;

    const token = localStorage.getItem('token');
    const requestKey = listKey;
    const offset = orders.length;
    setLoadingMore(true);
    try {
      const data = await requestOrdersPage(token, offset);
      if (loadedListKeyRef.current !== requestKey) return;

      const nextOrders = mergeOrdersById(orders, data);
      setOrders(nextOrders);
      const activeCount = tabCounts[activeTab];
      setHasMore(normalizedDebouncedSearch
        ? data.length === MY_ORDERS_PAGE_SIZE
        : (activeCount === null
          ? data.length === MY_ORDERS_PAGE_SIZE
          : nextOrders.length < activeCount));

      if (!normalizedDebouncedSearch) {
        saveOfflineMyOrdersList({
          userId,
          stage: selectedStage,
          tab: activeTab,
          orders: nextOrders
        }).catch((cacheError) => {
          console.warn('Не удалось сохранить следующую страницу заказов:', cacheError);
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки следующей страницы заказов:', error);
      showToast('Не удалось загрузить следующие заказы', 'error');
    } finally {
      if (loadedListKeyRef.current === requestKey) {
        setLoadingMore(false);
      }
    }
  }, [activeTab, hasMore, isDeferredStage, listKey, loading, loadingMore, normalizedDebouncedSearch, orders, readOnlyOffline, requestOrdersPage, selectedStage, showToast, tabCounts, userId]);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !hasMore || loading || loadingMore || readOnlyOffline || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      { rootMargin: '320px 0px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleLoadMore, hasMore, loading, loadingMore, readOnlyOffline]);

  // Назначить заказ себе
  const assignToMe = async (orderId) => {
    if (readOnlyOffline) {
      haptic('error');
      setConfirmModal(null);
      showToast('Изменения недоступны.', 'error');
      return;
    }

    haptic('tap');
    setAssigning(true);
    try {
      const token = localStorage.getItem('token');
      const isVirtualWashingStage = selectedStage === 'washing_laundry' || selectedStage === 'washing_cleaning';
      const response = await fetch(
        isVirtualWashingStage
          ? `${API_URL}/api/orders/${orderId}/washing-flows/${selectedStage}/take`
          : `${API_URL}/api/orders/${orderId}`,
        {
          method: isVirtualWashingStage ? 'POST' : 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: isVirtualWashingStage ? undefined : JSON.stringify({ assigned_to: userId })
        }
      );
      if (response.ok) {
        haptic('success');
        await reloadOrders(token);
        setConfirmModal(null);
        showToast(isVirtualWashingStage ? 'Поток взят в работу' : 'Заказ взят в работу', 'success');
        return;
      }

      let message = isVirtualWashingStage ? 'Не удалось взять поток в работу' : 'Не удалось взять заказ в работу';
      try {
        const errorData = await response.json();
        if (typeof errorData?.detail === 'string' && errorData.detail.trim()) {
          message = errorData.detail.trim();
        } else if (errorData?.detail?.message) {
          message = errorData.detail.message;
        }
      } catch (parseError) {
        console.warn('Не удалось прочитать ошибку назначения заказа:', parseError);
      }

      if (response.status === 409 || response.status === 403 || response.status === 400) {
        haptic('error');
        setConfirmModal(null);
        await reloadOrders(token);
        showToast(message, 'error');
        return;
      }

      haptic('error');
      showToast(message, 'error');
    } catch (error) {
      console.error('Ошибка назначения:', error);
      haptic('error');
      showToast('Не удалось взять заказ в работу', 'error');
    } finally {
      setAssigning(false);
    }
  };

  const stageConfig = STAGES_CONFIG[selectedStage];
  const stageColor = stageConfig?.color || 'gray';

  // Если этап не выбран - показываем выбор
  // Но если только один этап - пропускаем выбор
  if (!selectedStage) {
    if (stagesLoading) {
      return <MyOrdersStagesSkeleton />;
    }

    if (userRoles.length === 1 && !canViewDeferredOrders) {
      // Один этап - сразу открываем (загрузится через useEffect)
      return (
        <div className="space-y-6">
          <MyOrdersListSkeleton count={4} />
        </div>
      );
    }
    
    return (
      <div className="space-y-6 desktop-content-container">
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm">
          <div className="mb-4">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Выберите этап</div>
          </div>
          
          <div className="space-y-3">
            {USER_STAGE_ORDER
              .filter(stage => isDryCleaningEnabled || stage !== 'cleaning')
              .filter(stage => userRoles.includes(stage))
              .map((stage, index) => {
                const config = STAGES_CONFIG[stage];
                const Icon = config.icon;
                const stageStyles = STAGE_SELECTOR_STYLES[config.color] || STAGE_SELECTOR_STYLES.gray;
                const StageButton = (
                  <button
                    key={stage}
                    onClick={() => handleStageSelect(stage)}
                    className={`group relative w-full overflow-hidden rounded-2xl border px-4 py-3.5 text-left transition-all duration-150 hover:shadow-md active:scale-[0.99] ${stageStyles.row}`}
                  >
                    <span className={`absolute inset-y-2 left-2 w-1 rounded-full bg-gradient-to-b ${stageStyles.stripe}`} />
                    <div className="flex items-center gap-3">
                      <div className={`ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${stageStyles.icon}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={`truncate text-sm font-semibold ${stageStyles.text}`}>{config.label}</div>
                      </div>
                      <ChevronRight className={`h-4 w-4 shrink-0 transition-transform duration-150 group-hover:translate-x-0.5 ${stageStyles.arrow}`} />
                    </div>
                  </button>
                );

                if (animationsEnabled) {
                  return (
                    <motion.div
                      key={stage}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      {StageButton}
                    </motion.div>
                  );
                }

                return StageButton;
              })}

            {canViewDeferredOrders && (
              <div className="pt-2">
                <div className="mb-3 h-px w-full bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
                <button
                  type="button"
                  onClick={() => handleStageSelect('deferred')}
                  className="group relative w-full overflow-hidden rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3.5 text-left transition-all duration-150 hover:border-amber-300 hover:bg-amber-100/70 hover:shadow-md active:scale-[0.99] dark:border-amber-900/70 dark:bg-amber-950/25 dark:hover:bg-amber-950/40"
                >
                  <span className="absolute inset-y-2 left-2 w-1 rounded-full bg-gradient-to-b from-amber-500 to-orange-500" />
                  <div className="flex items-center gap-3">
                    <div className="ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-500 text-white shadow-[0_10px_24px_rgba(245,158,11,0.28)]">
                      {React.createElement(STAGES_CONFIG.deferred.icon, { className: 'h-5 w-5' })}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-amber-950 dark:text-amber-100">Отложенные</div>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-amber-500 transition-transform duration-150 group-hover:translate-x-0.5" />
                  </div>
                </button>
              </div>
            )}
          </div>

          {userRoles.length === 0 && !canViewDeferredOrders && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              Нет доступных этапов
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isDeferredStage) {
    if (stagesLoading) return <MyOrdersStagesSkeleton />;
    if (!canViewDeferredOrders) {
      return (
        <div className="rounded-2xl bg-white p-8 text-center shadow-sm dark:bg-gray-900">
          <SearchX className="mx-auto mb-3 h-10 w-10 text-gray-400" />
          <div className="font-medium text-gray-900 dark:text-gray-100">Очередь вам недоступна</div>
          <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">Отложенные задания видят диспетчеры и старшие роли.</div>
        </div>
      );
    }
    return <div className="desktop-content-container"><DeferredWorkQueue canAccessTasks={canAccessTasks} /></div>;
  }

  return (
    <div className={`space-y-3 desktop-content-container-wide ${animationsEnabled ? 'animate-fade-in' : ''}`}>
        {/* Вкладки - отделены от шапки */}
        <div className={`bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden ${animationsEnabled ? 'animate-slide-up' : ''}`} style={animationsEnabled ? { animationDelay: '0.15s' } : {}}>
          <div className="grid grid-cols-3 border-b border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => handleTabChange('my')}
              className={`py-3 px-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'my'
                  ? (STAGE_TAB_STYLES[stageColor] || STAGE_TAB_STYLES.blue)
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              <span className="inline-flex items-center justify-center gap-1.5">Мои {tabCounts.my !== null && <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] leading-none text-gray-600 dark:bg-gray-800 dark:text-gray-300">{tabCounts.my}</span>}</span>
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('available')}
              className={`py-3 px-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'available'
                  ? (STAGE_TAB_STYLES[stageColor] || STAGE_TAB_STYLES.blue)
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              <span className="inline-flex items-center justify-center gap-1.5">Доступные {tabCounts.available !== null && <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] leading-none text-gray-600 dark:bg-gray-800 dark:text-gray-300">{tabCounts.available}</span>}</span>
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('archive')}
              className={`py-3 px-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'archive'
                  ? (STAGE_TAB_STYLES[stageColor] || STAGE_TAB_STYLES.blue)
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              <span className="inline-flex items-center justify-center gap-1.5">Архив {tabCounts.archive !== null && <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] leading-none text-gray-600 dark:bg-gray-800 dark:text-gray-300">{tabCounts.archive}</span>}</span>
            </button>
          </div>
        </div>

      <div className={`relative ${animationsEnabled ? 'animate-slide-up' : ''}`} style={animationsEnabled ? { animationDelay: '0.18s' } : {}}>
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
        <input
          type="text"
          enterKeyHint="search"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Поиск по номеру или адресу"
          aria-label="Поиск заказов по номеру или адресу"
          autoComplete="off"
          className="h-11 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-10 text-sm text-gray-900 shadow-sm outline-none transition-colors placeholder:text-gray-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:border-blue-500 dark:focus:ring-blue-900/30"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            title="Очистить поиск"
            aria-label="Очистить поиск"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Список заказов */}
      <div className={`space-y-2 ${animationsEnabled ? 'animate-slide-up' : ''}`} style={animationsEnabled ? { animationDelay: '0.2s' } : {}}>
        {loading ? (
          <MyOrdersListSkeleton count={activeTab === 'available' ? 4 : 3} />
        ) : displayedOrders.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-sm text-center">
            <SearchX className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {normalizedSearchQuery
                ? 'Заказы не найдены'
                : activeTab === 'my'
                  ? 'У вас нет заказов на этом этапе'
                  : activeTab === 'available'
                    ? 'Нет доступных заказов'
                    : 'Архив пуст'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {normalizedSearchQuery
                ? 'Попробуйте изменить номер заказа или адрес'
                : activeTab === 'available'
                  ? 'Все заказы уже распределены'
                  : activeTab === 'my'
                    ? 'Возьмите заказ из вкладки "Доступные"'
                    : 'Здесь появятся заказы, где вы завершили выбранный этап'}
            </p>
          </div>
        ) : (
          <>
          {displayedOrders.map((order, index) => {
            const stageAddress = getStageCardAddress(order, selectedStage);
            const displayStageAddress = shortenDisplayAddress(stageAddress);
            const stageMapUrl = getYandexAddressUrl(stageAddress);
            const stageDateTime = getStageDateTimeParts(order, selectedStage === 'delivery' ? 'delivery' : 'pickup');
            const statusBadge = getRouteStatusBadge(order);
            const RouteClientTypeIcon = isCompanyOrder(order) ? BriefcaseBusiness : User;
            const archiveBadgeMeta = activeTab === 'archive' ? getArchiveStageBadgeMeta(order.status) : null;
            const ArchiveStageIcon = archiveBadgeMeta?.icon || null;
            const clientDisplayName = String(order.client_name || '').trim() || (isCompanyOrder(order) ? 'Юр. лицо' : 'Физ. лицо');
            const createdDateText = formatTimezoneDate(order.created_date, false);
            const pickupPlannedDate = getStageSchedule(order, 'pickup').date;
            const pickupPlannedTime = getStageSchedule(order, 'pickup').time;
            const deliveryPlannedTime = getStageSchedule(order, 'delivery').time;
            const pickupDateText = pickupPlannedDate ? formatTimezoneDate(pickupPlannedDate, false) : 'Дата не указана';
            const effectivePickupDateText = getEffectivePickupDate(order) ? formatTimezoneDate(getEffectivePickupDate(order), false) : 'Дата не указана';
            const pickupTimeText = formatTimeSlot(pickupPlannedTime) || 'Время не указано';
            const deliveryTimeText = formatTimeSlot(deliveryPlannedTime) || 'Время не указано';
            const itemsCount = order.items_count || 0;
            const completedItemsCount = order.stage_completed_items_count || 0;
            const shouldHighlightOverdue = activeTab === 'my' || activeTab === 'available';
            const openOrder = () => {
              haptic('tap');
              navigate(
                `/order/${order.id}?from=my-orders&tab=${activeTab}&stage=${selectedStage}`
              );
            };

            const renderStatsRow = (items) => (
              <div className="flex flex-wrap items-center gap-2 text-xs font-medium leading-4 text-gray-700 dark:text-gray-300">
                {items}
              </div>
            );

            const renderStatChip = (icon, value, title) => {
              const Icon = icon;
              const iconClassName =
                Icon === Check
                  ? 'text-green-600 dark:text-green-400'
                  : Icon === ShoppingCart
                    ? 'text-blue-600 dark:text-blue-400'
                    : Icon === Scale
                      ? 'text-amber-600 dark:text-amber-400'
                      : Icon === Ruler
                        ? 'text-violet-600 dark:text-violet-400'
                        : Icon === RotateCcw
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-gray-500 dark:text-gray-400';
              return (
                <span
                  className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-1.5 py-1 text-[11px] font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                  title={title}
                  aria-label={title}
                >
                  <Icon className={`h-3 w-3 shrink-0 ${iconClassName}`} />
                  <span>{value}</span>
                </span>
              );
            };

            const topMeta = (() => {
              if (selectedStage === 'washing' || selectedStage === 'drying_packing' || selectedStage === 'approval') {
                return {
                  primary: createdDateText,
                  secondary: order.self_delivery
                    ? 'Самопривоз'
                    : effectivePickupDateText,
                  primaryIcon: Calendar,
                  secondaryIcon: Truck,
                  primaryOverdue: false,
                  secondaryOverdue: false
                };
              }

              if (selectedStage === 'delivery') {
                const deliveryOverdueMeta = getStageOverdueMeta(order, 'delivery');
                return {
                  primary: getEffectiveDeliveryDate(order) ? formatTimezoneDate(getEffectiveDeliveryDate(order), false) : 'Дата не указана',
                  secondary: deliveryTimeText,
                  primaryIcon: Truck,
                  secondaryIcon: Clock,
                  primaryOverdue: shouldHighlightOverdue ? deliveryOverdueMeta.dateOverdue : false,
                  secondaryOverdue: shouldHighlightOverdue ? deliveryOverdueMeta.timeOverdue : false
                };
              }

              if (selectedStage === 'handover') {
                return {
                  primary: createdDateText,
                  secondary: order.self_delivery
                    ? 'Самопривоз'
                    : effectivePickupDateText,
                  primaryIcon: Calendar,
                  secondaryIcon: Truck,
                  primaryOverdue: false,
                  secondaryOverdue: false
                };
              }

              if (selectedStage === 'cleaning') {
                const cleaningOverdueMeta = getStageOverdueMeta(order, 'pickup');
                return {
                  primary: effectivePickupDateText,
                  secondary: formatTimeSlot(pickupPlannedTime || deliveryPlannedTime) || 'Время не указано',
                  primaryIcon: Truck,
                  secondaryIcon: Clock,
                  primaryOverdue: shouldHighlightOverdue ? cleaningOverdueMeta.dateOverdue : false,
                  secondaryOverdue: shouldHighlightOverdue ? cleaningOverdueMeta.timeOverdue : false
                };
              }

              const pickupOverdueMeta = getStageOverdueMeta(order, 'pickup');
              return {
                primary: order.self_delivery ? 'Самопривоз' : stageDateTime.dateText,
                secondary: order.self_delivery
                  ? ''
                  : stageDateTime.timeText,
                primaryIcon: order.self_delivery ? Truck : Truck,
                secondaryIcon: Clock,
                primaryOverdue: selectedStage === 'pickup' && shouldHighlightOverdue ? (!order.self_delivery && pickupOverdueMeta.dateOverdue) : false,
                secondaryOverdue: selectedStage === 'pickup' && shouldHighlightOverdue ? (!order.self_delivery && pickupOverdueMeta.timeOverdue) : false
              };
            })();
            const TopMetaPrimaryIcon = topMeta.primaryIcon || Calendar;
            const TopMetaSecondaryIcon = topMeta.secondaryIcon || Clock;

            const stageExtraContent = (() => {
              if (selectedStage === 'washing' || selectedStage === 'washing_laundry' || selectedStage === 'washing_cleaning') {
                return (
                  renderStatsRow(
                    <>
                      {renderStatChip(Check, `${completedItemsCount}/${itemsCount || 0}`, 'Готово')}
                      {order.active_rework && renderStatChip(RotateCcw, `${order.rework_items_count || 1}`, 'Доработка')}
                      {order.area_required_count > 0 && renderStatChip(Ruler, `${order.area_filled_count}/${order.area_required_count}`, 'Площадь')}
                      {order.weight_required_count > 0 && renderStatChip(Scale, `${order.weight_filled_count}/${order.weight_required_count}`, 'Вес')}
                    </>
                  )
                );
              }

              if (selectedStage === 'drying_packing') {
                return renderStatsRow(
                  <>
                    {renderStatChip(Check, `${completedItemsCount}/${itemsCount || 0}`, 'Готово')}
                    {order.active_rework && renderStatChip(RotateCcw, `${order.rework_items_count || 1}`, 'На перестире')}
                  </>
                );
              }

              if (selectedStage === 'approval') {
                return renderStatsRow(
                  <>
                    {renderStatChip(ShoppingCart, itemsCount, 'Предметов')}
                    {renderStatChip(
                      hasSavedApprovalReturnConditions(order) ? Check : XCircle,
                      hasSavedApprovalReturnConditions(order) ? 'Сохранены' : 'Не заданы',
                      'Условия возврата'
                    )}
                  </>
                );
              }

              if (selectedStage === 'delivery') {
                return renderStatsRow(renderStatChip(ShoppingCart, itemsCount, 'Предметов'));
              }

              if (selectedStage === 'handover') {
                return renderStatsRow(renderStatChip(ShoppingCart, itemsCount, 'Предметов'));
              }

              if (selectedStage === 'cleaning') {
                return renderStatsRow(
                  <>
                    {renderStatChip(Package, itemsCount, 'Предметов')}
                    {renderStatChip(Check, `${completedItemsCount}/${itemsCount || 0}`, 'Готово')}
                  </>
                );
              }

              if (selectedStage === 'pickup') {
                return null;
              }

              return renderStatsRow(renderStatChip(Package, itemsCount, 'Предметов'));
            })();

            const OrderCard = (
              <div
                key={order.id}
                role="button"
                tabIndex={0}
                onClick={openOrder}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openOrder();
                  }
                }}
                className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:border-gray-300 active:scale-[0.99] dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600"
              >
                <div className="px-4 pb-3 pt-2">
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className={`inline-flex h-6 items-center rounded-md px-1.5 text-[11px] font-medium leading-none ring-1 ${statusBadge.orderBadgeClass}`}>
                        № {order.order_number}
                      </span>
                      {activeTab === 'archive' && archiveBadgeMeta && (
                        <span
                          className={`inline-flex h-6 w-6 items-center justify-center rounded-md text-[11px] font-semibold ring-1 ring-gray-200 dark:ring-gray-700 ${archiveBadgeMeta.className}`}
                          title={archiveBadgeMeta.label}
                          aria-label={archiveBadgeMeta.label}
                        >
                          {ArchiveStageIcon ? <ArchiveStageIcon className={`h-3 w-3 ${archiveBadgeMeta.iconClassName || 'text-gray-500 dark:text-gray-400'}`} /> : <Check className="h-3 w-3 text-gray-500 dark:text-gray-400" />}
                        </span>
                      )}
                    </div>
                    <div className={`flex h-6 items-center justify-end gap-1.5 text-right text-[12px] font-medium leading-4 ${topMeta.primaryOverdue ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
                      <TopMetaPrimaryIcon className={`h-3 w-3 shrink-0 ${topMeta.primaryOverdue ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'}`} />
                      <span>{topMeta.primary}</span>
                    </div>
                    <div className="flex min-h-4 min-w-0 items-center gap-1.5 text-[11px] leading-4 text-gray-600 dark:text-gray-300">
                      <Building2 className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
                      <span className="min-w-0 truncate">{order.department_name || 'Подразделение не выбрано'}</span>
                    </div>
                    <div className={`flex min-h-4 items-center justify-end gap-1.5 text-right text-[11px] font-medium leading-4 ${topMeta.secondaryOverdue ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      <TopMetaSecondaryIcon className={`h-3 w-3 shrink-0 ${topMeta.secondaryOverdue ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'}`} />
                      <span>{topMeta.secondary}</span>
                    </div>
                  </div>

                  <div className="mt-2 border-t border-gray-200 dark:border-gray-700" />

                  <div className="mt-3 flex min-h-6 items-start justify-between gap-3">
                    <div className="mt-3 flex min-w-0 flex-1 items-center gap-1.5 text-[11px] leading-4 text-gray-600 dark:text-gray-300">
                      <RouteClientTypeIcon
                        className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500"
                        title={isCompanyOrder(order) ? 'Юридическое лицо' : 'Физическое лицо'}
                        aria-label={isCompanyOrder(order) ? 'Юридическое лицо' : 'Физическое лицо'}
                      />
                      <span className="min-w-0 truncate">{clientDisplayName}</span>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      {order.has_unread_comments && (
                        <span className="inline-flex h-6 min-w-6 items-center justify-center gap-1 rounded-lg bg-gray-100 px-1.5 text-[11px] font-semibold text-gray-700 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700">
                          <MessageCircle className="h-3 w-3 text-red-500 dark:text-red-400" />
                          {order.unread_comments_count || ''}
                        </span>
                      )}
                      {order.client_vip && (
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg border border-violet-700/70 bg-violet-50 text-violet-800 dark:border-violet-500/70 dark:bg-violet-950/30 dark:text-violet-200" title="VIP клиент">
                          <Star className="h-3 w-3 fill-current text-violet-700 dark:text-violet-300" />
                        </span>
                      )}
                      {order.priority === 'urgent' && (
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg border border-orange-700/70 bg-orange-50 text-orange-800 dark:border-orange-500/70 dark:bg-orange-950/30 dark:text-orange-200" title="Срочный заказ">
                          <Zap className="h-3 w-3 text-orange-700 dark:text-orange-300" />
                        </span>
                      )}
                      {order.emergency_dispatch && (
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg border border-fuchsia-700/70 bg-fuchsia-50 text-fuchsia-800 dark:border-fuchsia-500/70 dark:bg-fuchsia-950/30 dark:text-fuchsia-200" title="Экстренный выезд">
                          <Siren className="h-3 w-3 text-fuchsia-700 dark:text-fuchsia-300" />
                        </span>
                      )}
                      {activeTab !== 'archive' && order.active_rework && (
                        <span
                          className="inline-flex h-6 w-6 items-center justify-center rounded-lg border border-amber-700/70 bg-amber-50 text-amber-800 dark:border-amber-500/70 dark:bg-amber-950/30 dark:text-amber-200"
                          title={`Доработка · высокий приоритет${order.rework_items_count ? ` · предметов: ${order.rework_items_count}` : ''}`}
                          aria-label="Доработка · высокий приоритет"
                        >
                          <RotateCcw className="h-3 w-3 text-amber-700 dark:text-amber-300" />
                        </span>
                      )}
                      {activeTab !== 'archive' && hasDispatcherPriorityForStage(order, selectedStage) && (
                        <span
                          className="inline-flex h-6 w-6 items-center justify-center rounded-lg border border-emerald-700/70 bg-emerald-50 text-emerald-800 dark:border-emerald-500/70 dark:bg-emerald-950/30 dark:text-emerald-200"
                          title={
                            order.dispatcher_priority_scope === 'stage'
                              ? `Приоритет этапа: ${STAGES_CONFIG[order.dispatcher_priority_stage]?.label || order.dispatcher_priority_stage}`
                              : 'Диспетчерский приоритет'
                          }
                        >
                          <ArrowUp className="h-3 w-3 text-emerald-700 dark:text-emerald-300" />
                        </span>
                      )}
                      {activeTab !== 'archive' && hasDispatcherCallRequiredForStage(order, selectedStage) && (
                        <span
                          className="inline-flex h-6 w-6 items-center justify-center rounded-lg border border-sky-700/70 bg-sky-50 text-sky-800 dark:border-sky-500/70 dark:bg-sky-950/30 dark:text-sky-200"
                          title={selectedStage === 'pickup' ? 'Нужен созвон перед забором' : 'Нужен созвон перед доставкой клиенту'}
                        >
                          <Phone className="h-3 w-3 text-sky-700 dark:text-sky-300" />
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-1 flex items-start gap-1 text-[12px] font-medium leading-4 text-gray-950 dark:text-gray-50">
                    <MapPin className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
                    {stageMapUrl ? (
                      <button
                        type="button"
                        className="route-order-link-button m-0 min-w-0 p-0 text-left font-medium leading-4 text-gray-950 dark:text-gray-50"
                        onClick={(event) => {
                          event.stopPropagation();
                          haptic('tap');
                          setMapConfirmModal({ address: stageAddress, url: stageMapUrl });
                        }}
                      >
                        <span className="break-words">{displayStageAddress}</span>
                      </button>
                    ) : (
                      <span className="break-words">Адрес не указан</span>
                    )}
                  </div>

                  {stageExtraContent ? (
                    <div className="mt-2 space-y-1.5">
                      {stageExtraContent}
                    </div>
                  ) : null}
                </div>

                {activeTab === 'available' ? (
                  <div className="px-4 pb-3 pt-0">
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        if (readOnlyOffline) {
                          haptic('error');
                          showToast('Изменения недоступны.', 'error');
                          return;
                        }
                        haptic('tap');
                        setConfirmModal({ orderId: order.id, orderNumber: order.order_number });
                      }}
                      disabled={readOnlyOffline}
                      className={`route-order-action-button inline-flex h-[34px] min-h-[34px] w-full items-center justify-center gap-1.5 rounded-lg border border-green-500 bg-transparent px-3 text-xs font-semibold leading-none text-green-700 transition-colors dark:text-green-300 ${
                        readOnlyOffline
                          ? 'cursor-not-allowed opacity-55'
                          : 'hover:bg-green-50 dark:hover:bg-green-900/20'
                      }`}
                      title={readOnlyOffline ? 'Изменения недоступны' : 'Принять заказ'}
                    >
                      <Check className="h-3 w-3" />
                      <span>Принять заказ</span>
                    </button>
                  </div>
                ) : null}
              </div>
            );

            if (animationsEnabled) {
              return (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index % MY_ORDERS_PAGE_SIZE, 5) * 0.045 }}
                >
                  {OrderCard}
                </motion.div>
              );
            }

            return OrderCard;
          })}
          <div ref={loadMoreSentinelRef} className="min-h-[2px]" aria-live="polite" aria-busy={loadingMore}>
            {loadingMore ? (
              <MyOrdersListSkeleton count={2} />
            ) : null}
          </div>
          </>
        )}
      </div>

      {/* Модалка подтверждения */}
      <ModalShell
        open={Boolean(confirmModal)}
        onClose={() => setConfirmModal(null)}
        variant="sheet"
        panelClassName="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl dark:bg-gray-900"
      >
        {confirmModal && (
          <div>
            <div className="mb-3 flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-300">
                <Check className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Принять заказ?</h3>
                <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
                  Заказ <span className="font-semibold text-gray-900 dark:text-gray-100">№ {confirmModal.orderNumber}</span> будет назначен на вас.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  haptic('close');
                  setConfirmModal(null);
                }}
                disabled={assigning}
                className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={() => assignToMe(confirmModal.orderId)}
                disabled={assigning || readOnlyOffline}
                className="min-h-[40px] flex-1 rounded-lg bg-green-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {assigning ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>Назначаю...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Принять</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </ModalShell>

      {/* Модалка открытия карты */}
      <ModalShell
        open={Boolean(mapConfirmModal)}
        onClose={() => setMapConfirmModal(null)}
        variant="sheet"
        panelClassName="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl dark:bg-gray-900"
      >
        {mapConfirmModal && (
          <div>
            <div className="mb-3 flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300">
                <MapPin className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Открыть Яндекс.Карты?</h3>
                <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">{mapConfirmModal.address}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  haptic('close');
                  setMapConfirmModal(null);
                }}
                className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={() => {
                  haptic('open');
                  window.open(mapConfirmModal.url, '_blank', 'noopener,noreferrer');
                  setMapConfirmModal(null);
                }}
                className="min-h-[40px] flex-1 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
              >
                Открыть
              </button>
            </div>
          </div>
        )}
      </ModalShell>

          </div>
  );
};

export default MyOrders;
